import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DataCacheProvider } from './context/DataCacheContext';
import { QzTrayProvider } from './context/QzTrayContext';

// Page Imports
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import InventoryPage from './pages/InventoryPage';
import StockAuditMobilePage from './pages/StockAuditMobilePage';
import ProductionBatchPage from './pages/ProductionBatchPage';
import ProductionBatchDetailsPage from './pages/ProductionBatchDetailsPage';
import FabricSpecificationPage from './pages/FabricSpecificationPage';
import ProductsPage from './pages/ProductsPage';
import StockHousePage from './pages/StockHousePage';
import BundleDefinitionsPage from './pages/BundleDefinitionsPage';
import CollectionAutomationPage from './pages/CollectionAutomationPage';
import MarketingAnalyticsPage from './pages/MarketingAnalyticsPage';
import LookbookPage from './pages/LookbookPage';
import CollectionCreativesPage from './pages/CollectionCreativesPage';
import ReviewExamplesPage from './pages/ReviewExamplesPage';
import FacebookAdsPage from './pages/FacebookAdsPage';
import V9OpsPage from './pages/V9OpsPage';
import V9OrdersPage from './pages/V9OrdersPage';
import V9ActionQueuePage from './pages/V9ActionQueuePage';
import OrdersNewPage from './pages/OrdersNewPage';
import ProductDetailsPage from './pages/ProductDetailsPage';
import NewLookbookV2Page from './pages/NewLookbookV2Page';
import CustomerSupportPage from './pages/CustomerSupportPage';
import B2BExportPage from './pages/B2BExportPage';
import OrderDeskPage from './pages/OrderDeskPage';
import ShopifyAuditPage from './pages/ShopifyAuditPage';
import BlogPage from './pages/BlogPage';
import LookbookApprovalPage from './pages/LookbookApprovalPage';
import SharedEditorialsPage from './pages/SharedEditorialsPage';
import ChannelManagementPage from './pages/ChannelManagementPage';
import DiscountsPage from './pages/DiscountsPage';
import DiscountCalendarPage from './pages/DiscountCalendarPage';
import RepeatCustomerPage from './pages/RepeatCustomerPage';
import FreeGiftPage from './pages/FreeGiftPage';
import OffersPage from './pages/OffersPage';
import CodPincodeRiskPage from './pages/CodPincodeRiskPage';
import MetaAudiencesPage from './pages/MetaAudiencesPage';
import SeoEnginePage from './pages/SeoEnginePage';
import SocialStudioPage from './pages/SocialStudioPage';
import ShopifyThemePage from './pages/ShopifyThemePage';
import FulfillmentHubPage from './pages/fulfillment/FulfillmentHubPage';
import VendorBillsPage from './pages/VendorBillsPage';
import TransactionsReconciliationPage from './pages/TransactionsReconciliationPage';


// Component Imports
import PillNavigation from './components/PillNavigation';
import AdminLayout from './components/AdminLayout';
import UserManagementPage from './pages/UserManagementPage';
import SystemActionsPage from './components/SystemActionsPage';
import ProductBackfillPage from './pages/ProductBackfillPage';
import PrintJobsPage from './components/PrintJobsPage';
import ProductVideoPage from './pages/ProductVideoPage';
import AccessDeniedPage from './pages/AccessDeniedPage';
import QuickPackPage from './pages/QuickPackPage';
import './components/ViewAsBanner.css';

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error('Application render failed:', error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: '2rem', color: '#9d2e2e' }}>
          <h1>Application Error</h1>
          <p>{this.state.error.message || 'Something went wrong while loading this page.'}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

// A generic protected route that checks for login status and page-specific permissions.
function ProtectedRoute({ children, pageId, pageIds = [] }) {
  const { currentUser, canAccessPage } = useAuth();
  if (!currentUser) {
    return <Navigate to="/login" />;
  }
  const requiredPages = Array.isArray(pageIds) && pageIds.length > 0
    ? pageIds
    : (pageId ? [pageId] : []);
  const allowed = requiredPages.length === 0 || requiredPages.some((id) => canAccessPage(id));
  return allowed ? children : <AccessDeniedPage />;
}

function HomeRoute() {
  const { canAccessPage } = useAuth();

  if (canAccessPage('admin')) return <Dashboard />;
  if (canAccessPage('ordersNew')) return <Navigate to="/orders" replace />;
  if (canAccessPage('stockHouse')) return <Navigate to="/stock-house" replace />;
  if (canAccessPage('orders')) return <Navigate to="/stock-house" replace />;
  if (canAccessPage('inventory')) return <Navigate to="/inventory" replace />;
  if (canAccessPage('batches')) return <Navigate to="/batches" replace />;
  if (canAccessPage('products')) return <Navigate to="/products" replace />;
  if (canAccessPage('support')) return <Navigate to="/support" replace />;
  if (canAccessPage('offers')) return <Navigate to="/offers" replace />;
  if (canAccessPage('fulfillment')) return <Navigate to="/fulfillment" replace />;
  if (canAccessPage('orderDesk')) return <Navigate to="/order-desk" replace />;
  if (canAccessPage('v9ActionQueue')) return <Navigate to="/v9-action-queue" replace />;
  if (canAccessPage('v9Orders')) return <Navigate to="/v9-orders" replace />;
  if (canAccessPage('bundles')) return <Navigate to="/bundles" replace />;
  if (canAccessPage('lookbookApproval')) return <Navigate to="/lookbook-approval" replace />;
  if (canAccessPage('b2bExport')) return <Navigate to="/b2b-export" replace />;
  return <AccessDeniedPage />;
}

// A layout component that includes navigation for authenticated users.
function MainLayout({ children }) {
    const location = useLocation();
    // Don't show navigation on the login page or public shared routes
    if (location.pathname === '/login' || location.pathname.startsWith('/shared/')) {
        return <><ViewAsBanner />{children}</>;
    }
    return (
        <>
            <ViewAsBanner />
            <PillNavigation />
            <main>{children}</main>
        </>
    );
}

function ViewAsBanner() {
  const { currentUser, isImpersonating, stopViewAs } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = React.useState('');
  const [exiting, setExiting] = React.useState(false);
  if (!isImpersonating) return null;
  const exit = async () => {
    setExiting(true);
    setError('');
    try {
      await stopViewAs();
      navigate('/admin/users');
    } catch (err) {
      setError(err.message || 'Could not exit View as.');
    } finally {
      setExiting(false);
    }
  };
  return <div className="view-as-banner" role="status">
    <span>Viewing the app as <strong>{currentUser.email || currentUser.uid}</strong>. Actions make real changes.</span>
    {error && <span className="view-as-banner-error">{error}</span>}
    <button type="button" onClick={exit} disabled={exiting}>{exiting ? 'Exiting…' : 'Exit View as'}</button>
  </div>;
}

function IdentityDataCache({ children }) {
  const { currentUser } = useAuth();
  return <DataCacheProvider key={currentUser?.uid || 'signed-out'}>{children}</DataCacheProvider>;
}

// This component holds the routes and is rendered inside the AuthProvider
function AppRoutes() {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading application...</div>;
  }

  return (
    <Routes>
      <Route path="/login" element={currentUser ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/" element={<ProtectedRoute><HomeRoute /></ProtectedRoute>} />
      <Route
        path="/orders"
        element={<ProtectedRoute pageIds={['ordersNew', 'admin']}><OrdersNewPage /></ProtectedRoute>}
      />
      <Route
        path="/stock-house"
        element={<ProtectedRoute pageIds={['stockHouse', 'orders']}><StockHousePage /></ProtectedRoute>}
      />
      <Route
        path="/quick-pack"
        element={<ProtectedRoute pageIds={['stockHouse', 'orders']}><QuickPackPage /></ProtectedRoute>}
      />
      <Route path="/inventory" element={<ProtectedRoute pageId="inventory"><InventoryPage /></ProtectedRoute>} />
      <Route
        path="/stock-audit-mobile"
        element={<ProtectedRoute pageIds={['inventory', 'stockHouse', 'admin']}><StockAuditMobilePage /></ProtectedRoute>}
      />
      <Route path="/products" element={<ProtectedRoute pageId="products"><ProductsPage /></ProtectedRoute>} />

      <Route path="/collections-automation" element={<ProtectedRoute pageId="admin"><CollectionAutomationPage /></ProtectedRoute>} />
      <Route path="/bundles" element={<ProtectedRoute pageIds={['bundles', 'inventory']}><BundleDefinitionsPage /></ProtectedRoute>} />
      <Route path="/batches" element={<ProtectedRoute pageId="batches"><ProductionBatchPage /></ProtectedRoute>} />
      <Route path="/batches/:batchId" element={<ProtectedRoute pageId="batches"><ProductionBatchDetailsPage /></ProtectedRoute>} />
      <Route path="/v9-action-queue" element={<ProtectedRoute pageIds={['v9ActionQueue', 'admin']}><V9ActionQueuePage /></ProtectedRoute>} />
      {/* V9 Orders: accessible to v9Orders-page users directly (no nav link shown for non-admins) */}
      <Route path="/v9-orders" element={<ProtectedRoute pageIds={['v9Orders', 'admin']}><V9OrdersPage /></ProtectedRoute>} />
      {/* New Orders page: separate permission, shown in nav for ordersNew users */}
      <Route path="/new-orders" element={<ProtectedRoute pageIds={['ordersNew', 'admin']}><OrdersNewPage /></ProtectedRoute>} />
      <Route path="/support" element={<ProtectedRoute pageId="support"><CustomerSupportPage /></ProtectedRoute>} />
      <Route path="/b2b-export" element={<ProtectedRoute pageIds={['b2bExport', 'admin']}><B2BExportPage /></ProtectedRoute>} />
      <Route path="/order-desk" element={<ProtectedRoute pageIds={['orderDesk', 'admin']}><OrderDeskPage /></ProtectedRoute>} />
      <Route path="/lookbook-approval" element={<ProtectedRoute pageId="lookbookApproval"><LookbookApprovalPage /></ProtectedRoute>} />
      <Route path="/offers" element={<ProtectedRoute pageIds={['offers', 'admin']}><OffersPage /></ProtectedRoute>} />
      <Route path="/social-studio" element={<ProtectedRoute pageId="admin"><SocialStudioPage /></ProtectedRoute>} />
      <Route path="/fulfillment" element={<ProtectedRoute pageIds={['fulfillment', 'admin']}><FulfillmentHubPage /></ProtectedRoute>} />

      {/* Admin section with nested routes, protected by page access */}
      <Route path="/admin" element={<ProtectedRoute pageId="admin"><AdminLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="users" replace />} />
        <Route path="users" element={<ProtectedRoute pageId="users"><UserManagementPage /></ProtectedRoute>} />
        <Route path="system-actions" element={<ProtectedRoute pageId="admin"><SystemActionsPage /></ProtectedRoute>} />
        <Route path="product-backfill" element={<ProtectedRoute pageId="admin"><ProductBackfillPage /></ProtectedRoute>} />
        <Route path="print-jobs" element={<ProtectedRoute pageId="admin"><PrintJobsPage /></ProtectedRoute>} />
        <Route path="marketing-analytics" element={<ProtectedRoute pageId="admin"><MarketingAnalyticsPage /></ProtectedRoute>} />
        <Route path="lookbook" element={<ProtectedRoute pageId="admin"><LookbookPage /></ProtectedRoute>} />
        <Route path="collection-creatives" element={<ProtectedRoute pageId="admin"><CollectionCreativesPage /></ProtectedRoute>} />
        <Route path="facebook-ads" element={<ProtectedRoute pageId="admin"><FacebookAdsPage /></ProtectedRoute>} />
        <Route path="review-examples" element={<ProtectedRoute pageId="admin"><ReviewExamplesPage /></ProtectedRoute>} />
        <Route path="fabric-specs" element={<ProtectedRoute pageId="fabricSpecs"><FabricSpecificationPage /></ProtectedRoute>} />
        <Route path="v9-orders" element={<ProtectedRoute pageIds={['v9Orders', 'admin']}><V9OrdersPage /></ProtectedRoute>} />
        <Route path="v9-ops" element={<ProtectedRoute pageId="admin"><V9OpsPage /></ProtectedRoute>} />
        <Route path="v9-action-queue" element={<ProtectedRoute pageId="admin"><V9ActionQueuePage /></ProtectedRoute>} />
        <Route path="product-details" element={<ProtectedRoute pageId="admin"><ProductDetailsPage /></ProtectedRoute>} />
        <Route path="product-video" element={<ProtectedRoute pageId="admin"><ProductVideoPage /></ProtectedRoute>} />
        <Route path="shopify-audit" element={<ProtectedRoute pageId="admin"><ShopifyAuditPage /></ProtectedRoute>} />
        <Route path="shopify-theme" element={<ProtectedRoute pageId="admin"><ShopifyThemePage /></ProtectedRoute>} />
        <Route path="hero-mosaic" element={<Navigate to="/admin/shopify-theme?tab=hero-mosaic" replace />} />
        <Route path="new-lookbook" element={<ProtectedRoute pageId="admin"><NewLookbookV2Page /></ProtectedRoute>} />
        <Route path="blog" element={<ProtectedRoute pageId="admin"><BlogPage /></ProtectedRoute>} />
        <Route path="seo-engine" element={<ProtectedRoute pageId="admin"><SeoEnginePage /></ProtectedRoute>} />
        <Route path="social-studio" element={<ProtectedRoute pageId="admin"><SocialStudioPage /></ProtectedRoute>} />
        <Route path="channel-management" element={<ProtectedRoute pageId="admin"><ChannelManagementPage /></ProtectedRoute>} />
        <Route path="discounts" element={<ProtectedRoute pageId="admin"><DiscountsPage /></ProtectedRoute>} />
        <Route path="discount-calendar" element={<ProtectedRoute pageId="admin"><DiscountCalendarPage /></ProtectedRoute>} />
        <Route path="repeat-customer" element={<ProtectedRoute pageId="admin"><RepeatCustomerPage /></ProtectedRoute>} />
        <Route path="free-gift" element={<ProtectedRoute pageId="admin"><FreeGiftPage /></ProtectedRoute>} />
        <Route path="cod-pincode-risk" element={<ProtectedRoute pageId="admin"><CodPincodeRiskPage /></ProtectedRoute>} />
        <Route path="meta-audiences" element={<ProtectedRoute pageId="admin"><MetaAudiencesPage /></ProtectedRoute>} />
        <Route path="vendor-bills" element={<ProtectedRoute pageId="admin"><VendorBillsPage /></ProtectedRoute>} />
        <Route path="transactions-reconciliation" element={<ProtectedRoute pageId="admin"><TransactionsReconciliationPage /></ProtectedRoute>} />
      </Route>

      <Route path="/shared/editorials" element={<SharedEditorialsPage />} />
      <Route path="*" element={<Navigate to={currentUser ? "/" : "/login"} />} />
    </Routes>
  );
}

// The main App component now sets up the providers and the router.
function App() {
  return (
    <AppErrorBoundary>
      <Router>
        <AuthProvider>
          <IdentityDataCache>
            <QzTrayProvider>
              <MainLayout>
                <AppRoutes />
              </MainLayout>
            </QzTrayProvider>
          </IdentityDataCache>
        </AuthProvider>
      </Router>
    </AppErrorBoundary>
  );
}

export default App;
